import React, { useState, useEffect } from "react";
import axios from "utils/axios";
import { MdOutlinePhotoCamera } from "react-icons/md";
import { RxCross1 } from "react-icons/rx";
import { LiaCommentSolid } from "react-icons/lia";
import { FiDownload } from "react-icons/fi";
import { TiTick } from "react-icons/ti";
import Modal from "components/ui/Modal";
import CommentsBox from "components/ui/CommentsBox";
import Button from "components/ui/Button";
import CircularProgress from "components/ui/CircularProgress";

const TYPE_COLORS = {
  M: { bg: "bg-green-600", label: "main" },
  E: { bg: "bg-orange-600", label: "exposure" },
  P: { bg: "bg-blue-600", label: "portrait" },
};

const ImageContainer = ({
  image,
  onDelete,
  onPreview,
  viewMode = "grid",
  progress = 0,
  showDelete = true,
  showDownload = false,
  showTypeSelect = false,
  onTypeSelect,
  isSelected = false,
  eventId,
  propertyId,
  revisionType = "photo",
}) => {
  const [commentsVisible, setCommentsVisible] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [selectedType, setSelectedType] = useState(image.type || null);

  useEffect(() => {
    setImageError(false);
  }, [image.url]);

  useEffect(() => {
    setSelectedType(image.type || null);
  }, [image.type]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = image?.originalUrl || image?.url;
    link.download = image.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openCommentsAndUpdateStatus = async () => {
    setCommentsVisible(true);
    if (propertyId) {
      try {
        await axios.post("/admin/prepareMarketing/status", {
          eventId,
          propertyId,
          status: "revisionInProgress",
          type: revisionType,
        });
      } catch (error) {
        console.error("Error updating status:", error);
      }
    }
  };

  const handleTypeClick = (type) => {
    if (!onTypeSelect) return;
    if (selectedType === type) {
      const ok = onTypeSelect(image.id, null, false);
      if (ok) setSelectedType(null);
    } else {
      const ok = onTypeSelect(image.id, type, true);
      if (ok) setSelectedType(type);
    }
  };

  const isUploading = progress > 0 && progress < 100;

  const allComments = image?.comments || [];
  const revisionComments = allComments.filter((c) => c?.revisionId != null);
  const agentComments = allComments.filter((c) => c?.revisionId == null);
  const hasAgentComments = agentComments.length > 0;
  const hasPendingAgentComment = agentComments.some(
    (c) =>
      c.status === "revisionSent" ||
      c.status === "revisionInProgress" ||
      (!c.status && !c.responseRevisionId && !c.responseRevisionUrl)
  );
  const hasPendingRevision = revisionComments.some(
    (c) =>
      c.status === "revisionSent" ||
      (!c.status && !c.responseRevisionId && !c.responseRevisionUrl)
  );
  const hasUploadedRevision = revisionComments.some(
    (c) =>
      c.status === "editorUploaded" ||
      c.status === "revisionCompleted" ||
      (c.status === "revisionInProgress" && c.responseRevisionId) ||
      !!c.responseRevisionUrl
  );
  const showRevisionOverlay = revisionComments.length > 0;
  const pendingRevisionCount = revisionComments.filter(
    (c) =>
      c.status === "revisionSent" ||
      (!c.status && !c.responseRevisionId && !c.responseRevisionUrl)
  ).length;

  const typeConfig = selectedType ? TYPE_COLORS[selectedType] : null;

  return (
    <div className="relative group bg-lightgray rounded-md p-1">
      <div className="relative">
        <div className="flex justify-between items-center">
          <p className="text-center text-[10px] truncate">{image.name}</p>
          {showDownload && (
            <button
              onClick={handleDownload}
              className="p-1 hover:text-blue-500"
            >
              <FiDownload size={13} className="text-darkergray" />
            </button>
          )}
        </div>

        {image?.editType && (
          <div className="absolute top-6 left-0 z-10">
            <div className="bg-blue-500 text-white text-sm px-2 py-0.5 rounded-r-full">
              {image.editType}
            </div>
          </div>
        )}

        {/* Image type badge (M/E/P) — read-only indicator */}
        {image?.type && TYPE_COLORS[image.type] && !showTypeSelect && (
          <div className="absolute top-6 right-0 z-10">
            <div
              className={`${TYPE_COLORS[image.type].bg} text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-l-full`}
            >
              {TYPE_COLORS[image.type].label}
            </div>
          </div>
        )}

        {!imageError ? (
          <img
            src={image.url}
            alt={image.name}
            className={`${viewMode === "grid" ? "h-24" : "max-h-[400px]"
              } w-full object-cover cursor-pointer ${isUploading ? "opacity-50" : ""}`}
            onClick={() => onPreview?.(image.id)}
            onError={() => setImageError(true)}
          />
        ) : (
          <div
            className={`${viewMode === "grid" ? "h-24" : "max-h-[400px]"
              } w-full flex flex-col items-center justify-center bg-lightgray`}
          >
            <MdOutlinePhotoCamera className="text-2xl text-darkgray" />
          </div>
        )}

        {showTypeSelect && !imageError && (
          <div
            className={`cursor-pointer absolute bottom-0 left-0 right-0 p-0.5 bg-opacity-50 ${typeConfig ? typeConfig.bg : "bg-gray-600"
              } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center space-x-1">
              {isSelected && <TiTick className="text-white" />}
              <p className="text-white text-center text-[10px] truncate">
                {typeConfig ? typeConfig.label : "select"}
              </p>
            </div>
          </div>
        )}

        {showRevisionOverlay && hasPendingRevision && (
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-1 flex items-center justify-center gap-2">
            <p className="text-white text-xs truncate">
              {hasUploadedRevision && pendingRevisionCount > 0
                ? `${pendingRevisionCount} revision note${pendingRevisionCount !== 1 ? "s" : ""} pending`
                : "Revision requested"}
            </p>
            <div
              className="relative flex items-center cursor-pointer"
              onClick={openCommentsAndUpdateStatus}
            >
              <LiaCommentSolid className="text-white w-5 h-5" />
              <span className="absolute top-0.5 right-0 w-1.5 h-1.5 bg-red-500 rounded-full" />
            </div>
          </div>
        )}

        {showRevisionOverlay && !hasPendingRevision && hasUploadedRevision && (
          <div className="absolute bottom-0 left-0 right-0 bg-green-700 bg-opacity-90 p-1 flex items-center justify-center gap-1.5">
            <TiTick className="text-white w-3.5 h-3.5 shrink-0" />
            <p className="text-white text-[10px] font-semibold truncate">
              Editor uploaded
            </p>
            <div
              className="flex items-center cursor-pointer ml-auto"
              onClick={() => setCommentsVisible(true)}
            >
              <LiaCommentSolid className="text-white w-4 h-4" />
            </div>
          </div>
        )}

        {!showRevisionOverlay && hasAgentComments && (
          <div
            className={`absolute bottom-0 left-0 right-0 p-1 flex items-center justify-center gap-2 ${
              hasPendingAgentComment
                ? "bg-black bg-opacity-50"
                : "bg-amber-700 bg-opacity-90"
            }`}
          >
            <p className="text-white text-[10px] font-semibold truncate">
              {hasPendingAgentComment ? "Revision requested" : "Agent comment"}
            </p>
            <div
              className="relative flex items-center cursor-pointer ml-auto"
              onClick={
                hasPendingAgentComment
                  ? openCommentsAndUpdateStatus
                  : () => setCommentsVisible(true)
              }
            >
              <LiaCommentSolid className="text-white w-4 h-4" />
              {hasPendingAgentComment && (
                <span className="absolute top-0.5 right-0 w-1.5 h-1.5 bg-red-500 rounded-full" />
              )}
            </div>
          </div>
        )}

        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <CircularProgress value={progress} />
          </div>
        )}

        {showDelete && !showDownload && (
          <div className="absolute -top-1.5 -right-1.5 flex space-x-2 items-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(image.id, image.folder);
              }}
              className="bg-mediumgray rounded-full p-1 hover:bg-darkgray"
            >
              <RxCross1 className="text-black text-xs" />
            </button>
          </div>
        )}
      </div>

      {showTypeSelect && (
        <div className="flex space-x-1 pt-1">
          {["M", "E", "P"].map((type) => (
            <Button
              key={type}
              className={`w-24 border border-mediumgray text-xs px-2 py-1 ${selectedType === type
                  ? `${TYPE_COLORS[type].bg} text-white`
                  : "bg-white"
                }`}
              onClick={(e) => {
                e.stopPropagation();
                handleTypeClick(type);
              }}
            >
              {type}
            </Button>
          ))}
        </div>
      )}

      <Modal
        isOpen={commentsVisible}
        onClose={() => setCommentsVisible(false)}
        title="Comments"
      >
        <CommentsBox comments={image?.comments} imageId={image.id} />
      </Modal>
    </div>
  );
};

export default ImageContainer;
